<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { usePolicy } from 'dashboard/composables/usePolicy';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  updatedAt: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['action', 'open']);
const { checkPermissions } = usePolicy();

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const menuItems = computed(() => {
  const allOptions = [
    {
      label: t('CAPTAIN.ASSISTANTS.OPTIONS.VIEW_CONNECTED_INBOXES'),
      value: 'viewConnectedInboxes',
      action: 'viewConnectedInboxes',
      icon: 'i-lucide-link',
    },
  ];

  if (checkPermissions(['administrator'])) {
    allOptions.push(
      {
        label: t('CAPTAIN.ASSISTANTS.OPTIONS.EDIT_ASSISTANT'),
        value: 'edit',
        action: 'edit',
        icon: 'i-lucide-pencil-line',
      },
      {
        label: t('CAPTAIN.ASSISTANTS.OPTIONS.DELETE_ASSISTANT'),
        value: 'delete',
        action: 'delete',
        icon: 'i-lucide-trash',
      }
    );
  }

  return allOptions;
});

const lastUpdatedAt = computed(() =>
  t('CAPTAIN.ASSISTANTS.UPDATED_AT', { time: dynamicTime(props.updatedAt) })
);

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};
</script>

<template>
  <CardLayout>
    <div class="flex w-full items-start gap-3">
      <span
        aria-hidden="true"
        class="grid text-white shadow-sm size-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-n-violet-9 to-n-violet-10"
      >
        <span class="i-lucide-bot size-5" />
      </span>

      <button
        type="button"
        class="min-w-0 flex-1 text-start ltr:text-left rtl:text-right"
        @click="emit('open', id)"
      >
        <h6
          class="text-base font-medium truncate transition-colors text-n-slate-12 hover:underline"
        >
          {{ name }}
        </h6>
        <p class="mt-1 mb-0 text-sm text-n-slate-11 line-clamp-2">
          {{ description || t('CAPTAIN.ASSISTANTS.NO_DESCRIPTION') }}
        </p>
      </button>

      <div
        v-on-clickaway="() => toggleDropdown(false)"
        class="relative flex items-center group"
      >
        <Button
          icon="i-lucide-ellipsis-vertical"
          color="slate"
          variant="ghost"
          size="xs"
          :aria-label="t('CAPTAIN.ASSISTANTS.OPTIONS.LABEL')"
          class="rounded-md group-hover:bg-n-alpha-2"
          @click="toggleDropdown()"
        />
        <DropdownMenu
          v-if="showActionsDropdown"
          :menu-items="menuItems"
          class="mt-1 ltr:right-0 rtl:left-0 top-full"
          @action="handleAction($event)"
        />
      </div>
    </div>

    <span class="text-xs text-n-slate-10">
      {{ lastUpdatedAt }}
    </span>
  </CardLayout>
</template>
